import db from '../services/db';

const VAPID_PUBLIC = (import.meta.env.VITE_VAPID_PUBLIC_KEY || '').trim();

export function isPushSupported() {
    return typeof window !== 'undefined'
        && 'serviceWorker' in navigator
        && 'PushManager' in window
        && 'Notification' in window;
}

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
    return out;
}

export async function ensureServiceWorker() {
    if (!('serviceWorker' in navigator)) return null;
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;
    return reg;
}

export async function subscribeToPush(userId) {
    if (!isPushSupported()) return { ok: false, error: 'Push not supported in this browser' };
    if (!VAPID_PUBLIC) return { ok: false, error: 'Push is not configured (missing VAPID public key)' };
    if (!userId) return { ok: false, error: 'Not signed in' };

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { ok: false, error: 'Permission denied' };

    const reg = await ensureServiceWorker();
    if (!reg) return { ok: false, error: 'Service worker unavailable' };

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC),
        });
    }

    const json = sub.toJSON();
    const saved = await db.savePushSubscription(userId, json);
    if (!saved) return { ok: false, error: 'Could not save subscription' };
    try {
        await db.notifyMessagePushEnabled(userId);
    } catch (err) {
        console.warn('[Push] teacher notify failed:', err?.message || err);
    }
    return { ok: true, subscription: json };
}

export async function unsubscribeFromPush() {
    if (!isPushSupported()) return true;
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager?.getSubscription();
    if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await db.deletePushSubscription(endpoint);
    }
    return true;
}

/** Desktop / tab-open Notification API (no SW required). */
export function showBrowserNotification(title, options = {}) {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    try {
        // Avoid notifying when tab is focused and user already sees the toast
        if (document.visibilityState === 'visible') return;
        new Notification(title, {
            icon: '/apple-touch-icon.png',
            badge: '/favicon.png',
            ...options,
        });
    } catch {
        /* ignore */
    }
}
